import React, { useEffect, useState } from 'react';
import { Button, Input, Form, Select, message, Checkbox } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import './NewAddress.scss';
import {
  AddressItem,
  UpdateProfileDTO,
} from 'types/dto/order';
import i18n from 'i18n';
import { fetchDistrict } from 'services/profileService';
import { fetchAddress, saveAddress } from 'services/orderService';
import { convertObjectToOptions } from 'utils/commonUtils';
import { CommonOption } from 'types/common';
import NeedHelp from 'components/needHelp/NeedHelp';
import { SecurityUtils } from 'utils/securityUtils';
import { MemberProfile } from 'types/dto/profile';
import { useOutletContext } from 'react-router-dom';

interface NewAddressProps {
  //close drawer
  onClose?: () => void;
  //back to address management
  onBack?: () => void;
  //save callback to update address list
  onSave?: () => void;
  //if NewAddress is opened from AddressManagement, need transition animation
  needTransitionAnimation?: boolean;
  backButtonText?: string;
  title?: string;
  showDefaultCheckbox?: boolean;
  defaultAddressValue?: boolean;
}

const NewAddress: React.FC<NewAddressProps> = ({
  onClose,
  onBack,
  onSave,
  needTransitionAnimation = true,
  backButtonText,
  title,
  showDefaultCheckbox = true,
  defaultAddressValue = false,
}) => {
  const { t } = useTranslation('new_order');
  const lang = i18n.language;
  const [form] = Form.useForm<AddressItem>();
  const [isExiting, setIsExiting] = useState(false);
  const [districtOptions, setDistrictOptions] = useState<
    CommonOption[]
  >([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDefaultAddress, setIsDefaultAddress] = useState(defaultAddressValue);
  const { profile } = useOutletContext<{ profile: MemberProfile | null }>();

  useEffect(() => {
    const initData = async () => {
      try {
        const districtData = await fetchDistrict('/common', lang);
        setDistrictOptions(convertObjectToOptions(districtData));
      } catch (err) {
        console.error('Failed to obtain the service type:', err);
      }
    };
    initData();
  }, [lang]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      try {
        setIsSubmitting(true);

        const addresses: AddressItem[] = await fetchAddress('/customer', {
          juicyAppUniqueId: profile?.loyalty_account?.unique_id ?? '',
          lang: lang,
        });

        if (addresses.length >= 3) {
          message.error(t('maximumAddressesExceeded'));
          setIsSubmitting(false);
          return;
        }

        const newAddress = {
          ...values,
          seq: isDefaultAddress ? 1 : addresses.length + 1,
          addressID: -1,
          //the property names are different.
          //updateAddress dto : districtCode, getaddress dto: discde
          districtCode: values.discde,
        };

        let finalValues: AddressItem[] = [];
        if (isDefaultAddress) {
          const newOrderAddress = addresses.map((addr) => ({
            ...addr,
            seq: addr.seq + 1,
          }));
          finalValues = [newAddress, ...newOrderAddress];
        } else {
          finalValues = [...addresses, newAddress];
        }

        await saveAddress('/profile', {
          juicyAppUniqueId: profile?.loyalty_account?.unique_id ?? '',
          addresses: finalValues,
        } as UpdateProfileDTO);

        setIsSubmitting(false);

        if (needTransitionAnimation) {
          slideOut();
        }

        form.resetFields();
        setIsDefaultAddress(false);

        onSave?.();
      } catch (err) {
        setIsSubmitting(false);
        message.error(t('saveAddressFailed'));
      }
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setIsDefaultAddress(false);
    if (needTransitionAnimation) {
      slideOut();
    } else {
      onClose?.();
    }
  };

  const slideOut = () => {
    setIsExiting(true);
    setTimeout(() => {
      setIsExiting(false);
      onBack?.();
    }, 300);
  };

  const handleClose = () => {
    form.resetFields();
    setIsDefaultAddress(false);
    onClose?.();
  };

  return (
    <div
      className={`new-address-overlay ${needTransitionAnimation ? (isExiting ? 'exiting' : 'visiable') : ''
        } `}
    >
      <div className="new-address-container">
        <div className="new-address-content">
          {/* Header */}
          <div className="drawer-header">
            <div className="close-button-container">
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={handleClose}
                className="close-button"
              />
            </div>
            <div className="drawer-title">
              {title || t('pickUpDeliveryAddressRequired')}
            </div>
            <div className="drawer-description">
              {t('pleaseProvideAddressForPickUpDelivery')}
            </div>
          </div>

          {/* Address Form */}
          <div className="address-form">
            <Form
              form={form}
              layout="vertical"
              validateTrigger="onSubmit"
            >
              {/* Room and Floor */}
              <Form.Item
                name="address"
                label={t('address')}
                getValueFromEvent={(e) => SecurityUtils.sanitize(e.target.value, 255)}
                rules={[
                  {
                    required: true,
                    message: t('roomAndFloorRequired'),
                  },
                ]}
                className="form-item"
              >
                <Input
                  placeholder={t('addressPlaceholder')}
                  className="address-input"
                />
              </Form.Item>

              {/* District */}
              <Form.Item
                name="discde"
                label={t('district')}
                rules={[
                  {
                    required: true,
                    message: t('districtRequired'),
                  },
                ]}
                className="form-item"
              >
                <Select
                  placeholder={t('districtPlaceholder')}
                  className="address-input"
                  options={districtOptions}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>

              {showDefaultCheckbox && (
                <div>
                  <Checkbox checked={isDefaultAddress} onChange={(event) => {
                    setIsDefaultAddress(event.target.checked);

                  }}>{t('setAsDefaultAddress')}</Checkbox>
                </div>
              )}
            </Form>
          </div>
        </div>

        {/* Footer */}
        <div className="new-address-footer">
          <NeedHelp />

          <div className="button-section">
            <Button
              size="large"
              onClick={handleCancel}
              className="cancel-button"
              color="primary"
              variant="outlined"
              disabled={isSubmitting}
            >
              {backButtonText || t('cancel')}
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={handleSave}
              className="save-button"
              loading={isSubmitting}
            >
              {t('save')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewAddress;
